/**
 * `/projects/:id/waivers` — the waiver register (`Waivers.dc.html`, §3.8).
 *
 * The API for this has existed since Phase 3; the screen had not been built, so there was
 * no way to raise a waiver at all. The audit workspace offered a WAIVED button whose
 * tooltip said "Waivers arrive in Phase 3" and which the server refused, because a waiver
 * has to exist and be approved before an item can be set to it.
 *
 * Two things this screen must never soften:
 *
 *   · **a waiver does not raise the score.** A waived item stays in the denominator and
 *     counts as unmet (§6.4). It records that somebody accepted the gap, and who.
 *   · **one signature is not approval.** It takes two people in two different roles, and
 *     until the second arrives the waiver covers nothing — the item is still Pending and
 *     its gate question is still open.
 *
 * `canSign` and `cannotSignReason` are computed server-side by the same `canSign` the API
 * enforces with, so the greyed button here gives the reason the server would give.
 */

import { useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { WAIVER_APPROVAL_ROLES, teamRoleName } from '@portal/contracts';
import type { TeamRole_, Waiver } from '@portal/contracts';
import { Avatar, PermissionButton, WarningGlyph } from '@portal/ui';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const CHIP: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 20,
  padding: '0 6px',
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--bd)',
  background: 'var(--neb)',
  color: 'var(--mu)',
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const INPUT: CSSProperties = {
  height: 30,
  padding: '0 9px',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-md)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  font: '400 12.5px/1 var(--font-sans)',
  width: '100%',
};

/** How each effect paints. The row's colour is the answer to "is this doing anything?". */
const EFFECT: Record<Waiver['effect'], { row: string; chip: CSSProperties; label: string }> = {
  COVERING: {
    row: 'transparent',
    chip: { background: 'var(--wvb)', color: 'var(--wv)', borderColor: '#DCC9F7' },
    label: 'Covering',
  },
  EXPIRING_SOON: {
    row: 'var(--wab)',
    chip: { background: 'var(--wab)', color: 'var(--wa)', borderColor: 'var(--wabd)' },
    label: 'Expiring',
  },
  EXPIRED: {
    row: 'var(--bab)',
    chip: { background: 'var(--bab)', color: 'var(--ba)', borderColor: 'var(--babd)' },
    label: 'Expired',
  },
  NOT_COVERING: {
    row: 'transparent',
    chip: { background: 'var(--neb)', color: 'var(--mu)', borderColor: 'var(--bd)' },
    label: 'Not covering',
  },
};

function Card({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--r-lg)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '11px 14px',
          borderBottom: '1px solid var(--bd)',
        }}
      >
        <h2 style={{ margin: 0, font: '600 13px/1.3 var(--font-sans)' }}>{title}</h2>
        {action !== undefined && <span style={{ marginLeft: 'auto' }}>{action}</span>}
      </div>
      {children}
    </section>
  );
}

/** A signature slot, filled or not. The empty one is the whole point of the row. */
function Signature({ filled, children }: { filled: boolean; children: ReactNode }) {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        color: filled ? 'var(--tx)' : 'var(--wa)',
      }}
    >
      <span
        style={{
          width: 11,
          height: 11,
          borderRadius: 2,
          flex: '0 0 11px',
          border: filled ? '1px solid var(--ok)' : '1px dashed var(--wa)',
          background: filled ? 'var(--ok)' : 'transparent',
        }}
      />
      {children}
    </span>
  );
}

export function WaiversRoute() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  // Arriving from an item in the audit workspace: the form opens on that ref.
  const prefilledRef = params.get('item');
  const prefilledAudit = params.get('audit');

  const [requesting, setRequesting] = useState(prefilledRef !== null);
  const [itemRef, setItemRef] = useState(prefilledRef ?? '');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [signAsRole, setSignAsRole] = useState<TeamRole_ | ''>('');

  const waivers = useQuery({
    queryKey: ['waivers', id],
    queryFn: () => api.waivers(id),
    enabled: id !== '',
  });
  const project = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.project(id),
    enabled: id !== '',
  });
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });

  /**
   * The roles this person actually holds on THIS project.
   *
   * `signAsRole` is not a free choice: it is a claim about who you are on this project,
   * and the API refuses a role you do not hold. Offering the full list of six would
   * invite a rejection the person could do nothing about.
   */
  const myRoles = useMemo<readonly TeamRole_[]>(
    () => me.data?.memberships.find((entry) => entry.projectId === id)?.roles ?? [],
    [me.data, id],
  );

  const afterWrite = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['waivers', id] });
    await queryClient.invalidateQueries({ queryKey: ['project', id] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.createWaiver(id, {
        projectId: id,
        auditId: prefilledAudit,
        itemRef,
        reason,
        expiresAt: expiresAt === '' ? null : new Date(expiresAt).toISOString(),
        signAsRole: signAsRole as TeamRole_,
      }),
    onSuccess: async () => {
      setRequesting(false);
      setItemRef('');
      setReason('');
      setExpiresAt('');
      await afterWrite();
    },
  });

  const sign = useMutation({
    mutationFn: (input: { waiverId: string; role: TeamRole_ }) =>
      api.signWaiver(id, input.waiverId, input.role),
    onSuccess: afterWrite,
  });

  const reject = useMutation({
    mutationFn: (waiverId: string) => api.rejectWaiver(id, waiverId),
    onSuccess: afterWrite,
  });

  if (waivers.isPending) {
    return <div style={{ padding: '24px 28px', color: 'var(--mu)', fontSize: 13 }}>Loading…</div>;
  }

  if (waivers.isError || waivers.data === undefined) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 620, fontSize: 13, color: 'var(--ba)' }}>
        {waivers.error instanceof ApiClientError
          ? waivers.error.message
          : 'The waiver register could not be loaded.'}
      </div>
    );
  }

  const data = waivers.data;

  /** A signing role this person holds that is also allowed to approve (§3.8: PM or PO). */
  const approvalRole = myRoles.find((role) => WAIVER_APPROVAL_ROLES.includes(role));

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 12,
            color: 'var(--mu)',
          }}
        >
          <Link to="/">Portfolio</Link>
          <span>›</span>
          <Link to={`/projects/${id}`}>{project.data?.project.name ?? 'Project'}</Link>
          <span>›</span>
          <span style={{ color: 'var(--tx)', fontWeight: 600 }}>Waivers</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1
              style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)', letterSpacing: '-0.01em' }}
            >
              Waivers
            </h1>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ ...CHIP, ...EFFECT.COVERING.chip }}>
                {data.counts.covering} covering
              </span>
              <span style={{ ...CHIP, ...EFFECT.EXPIRING_SOON.chip }}>
                {data.counts.expiringSoon} expiring
              </span>
              <span style={{ ...CHIP, ...EFFECT.EXPIRED.chip }}>{data.counts.expired} expired</span>
              <span style={CHIP}>{data.counts.awaitingSignature} awaiting a signature</span>
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <PermissionButton
              variant="primary"
              onClick={() => setRequesting(true)}
              disabledReason={
                requesting
                  ? 'The form is already open.'
                  : myRoles.length === 0
                    ? 'Only members of this project can raise a waiver.'
                    : null
              }
            >
              Request a waiver
            </PermissionButton>
          </div>
        </div>
      </header>

      {/*
        The honesty note from the artboard, in the form rather than in the docs. People
        raise waivers hoping the number will move; it will not, and finding that out after
        the fact is how a compliance tool loses its credibility.
      */}
      {requesting && (
        <Card title="Request a waiver">
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 11 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                background: 'var(--wvb)',
                border: '1px solid #DCC9F7',
                borderRadius: 'var(--r-lg)',
                padding: '9px 11px',
              }}
            >
              <WarningGlyph size={14} />
              <span style={{ fontSize: 11.5, lineHeight: 1.55, color: 'var(--tx)' }}>
                <b>This will not raise the score.</b> A waived item stays in the denominator and
                counts as unmet. It records that somebody accepted the gap, and who — nothing more.
              </span>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={LABEL}>Item</span>
              <input
                value={itemRef}
                onChange={(event) => setItemRef(event.target.value.toUpperCase())}
                placeholder="D-08"
                className="mono"
                style={{ ...INPUT, maxWidth: 120 }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={LABEL}>Why we accept not meeting it</span>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                placeholder="Legacy pipeline on TeamCity 2019 cannot enforce a gate. Migration to the shared runner is in the Q4 plan."
                style={{
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--r-lg)',
                  background: 'var(--sf)',
                  color: 'var(--tx)',
                  font: '400 12.5px/1.5 var(--font-sans)',
                  padding: '8px 10px',
                  resize: 'vertical',
                }}
              />
              <span style={{ fontSize: 10.5, color: 'var(--mu)' }}>
                Required, at least a sentence. This text appears in the audit report and the
                client-facing summary.
              </span>
            </label>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 170 }}>
                <span style={LABEL}>Expires</span>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(event) => setExpiresAt(event.target.value)}
                  style={INPUT}
                />
                <span style={{ fontSize: 10.5, color: 'var(--mu)' }}>
                  Optional. On expiry the item returns to Pending on its own.
                </span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 220 }}>
                <span style={LABEL}>You sign as</span>
                <select
                  value={signAsRole}
                  onChange={(event) => setSignAsRole(event.target.value as TeamRole_)}
                  style={INPUT}
                >
                  <option value="">Choose your role…</option>
                  {myRoles.map((role) => (
                    <option key={role} value={role}>
                      {role} — {teamRoleName(role)}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 10.5, color: 'var(--mu)' }}>
                  Only roles you hold on this project. A signature counts towards approval only from{' '}
                  {WAIVER_APPROVAL_ROLES.join(' or ')}; raising it in another role records the
                  request and leaves both signatures outstanding.
                </span>
              </label>
            </div>

            {create.isError && (
              <div style={{ fontSize: 11.5, color: 'var(--ba)', lineHeight: 1.5 }}>
                {create.error instanceof ApiClientError
                  ? create.error.message
                  : 'The waiver was not created.'}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <PermissionButton
                variant="primary"
                onClick={() => create.mutate()}
                disabledReason={
                  itemRef.trim() === ''
                    ? 'Name the item this waiver covers.'
                    : reason.trim().length < 10
                      ? 'A reason of at least a sentence is required.'
                      : signAsRole === ''
                        ? 'Choose the role you are signing in.'
                        : create.isPending
                          ? 'Saving…'
                          : null
                }
              >
                {signAsRole === '' ? 'Submit' : `Submit and sign as ${signAsRole}`}
              </PermissionButton>
              <PermissionButton onClick={() => setRequesting(false)}>Cancel</PermissionButton>
            </div>
          </div>
        </Card>
      )}

      <Card title="Waivers">
        {data.waivers.length === 0 ? (
          <p
            style={{ margin: 0, padding: 14, fontSize: 12.5, color: 'var(--mu)', lineHeight: 1.55 }}
          >
            No waivers on this project. That is the good state — a waiver is an accepted gap, not a
            solved one.
          </p>
        ) : (
          data.waivers.map((waiver) => {
            const effect = EFFECT[waiver.effect];
            return (
              <div
                key={waiver.id}
                style={{
                  display: 'flex',
                  gap: 12,
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--bd)',
                  background: effect.row,
                }}
              >
                <span
                  className="mono"
                  style={{ width: 44, flex: '0 0 44px', fontSize: 11.5, fontWeight: 600 }}
                >
                  {waiver.itemRef}
                </span>

                <div
                  style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
                >
                  <span style={{ fontSize: 12.5, fontWeight: 500 }}>{waiver.statement}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--mu)', lineHeight: 1.5 }}>
                    &ldquo;{waiver.reason}&rdquo;
                  </span>
                  {waiver.effect === 'EXPIRED' && (
                    <span style={{ fontSize: 11, color: 'var(--ba)', lineHeight: 1.5 }}>
                      <b>Expired.</b> {waiver.itemRef} has returned to Pending and its gate question
                      is open again. Signatures do not survive expiry — a renewal needs two fresh
                      ones.
                    </span>
                  )}
                  {waiver.status === 'REQUESTED' && (
                    <span style={{ fontSize: 11, color: 'var(--ac)', lineHeight: 1.5 }}>
                      Waiting for a second signature, so it is covering nothing yet.{' '}
                      {waiver.cannotSignReason ?? 'You can provide it.'}
                    </span>
                  )}
                </div>

                <div
                  style={{
                    width: 104,
                    flex: '0 0 104px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5 }}>
                    <Avatar
                      initials={waiver.requestedBy.initials}
                      name={waiver.requestedBy.displayName}
                    />
                    {waiver.requestedBy.displayName}
                  </span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--mu)' }}>
                    {waiver.requestedAt.slice(0, 10)}
                  </span>
                </div>

                <div
                  style={{
                    width: 110,
                    flex: '0 0 110px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <span className="mono" style={{ fontSize: 11.5 }}>
                    {waiver.expiresAt === null ? 'no expiry' : waiver.expiresAt.slice(0, 10)}
                  </span>
                  <span style={{ ...CHIP, ...effect.chip, height: 18, fontSize: 10 }}>
                    {waiver.daysUntilExpiry !== null && waiver.effect === 'EXPIRING_SOON'
                      ? `in ${waiver.daysUntilExpiry} days`
                      : effect.label}
                  </span>
                </div>

                <div
                  style={{
                    width: 148,
                    flex: '0 0 148px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  {waiver.signatures.map((signature) => (
                    <Signature key={signature.role} filled>
                      {signature.user.displayName} · {signature.role}
                    </Signature>
                  ))}
                  {waiver.status === 'REQUESTED' && (
                    <>
                      <Signature filled={false}>Second signature</Signature>
                      <PermissionButton
                        variant="primary"
                        onClick={() =>
                          approvalRole !== undefined &&
                          sign.mutate({ waiverId: waiver.id, role: approvalRole })
                        }
                        disabledReason={
                          !waiver.canSign
                            ? (waiver.cannotSignReason ?? 'You cannot sign this waiver.')
                            : approvalRole === undefined
                              ? `A signature towards approval has to come from ${WAIVER_APPROVAL_ROLES.join(' or ')}.`
                              : sign.isPending
                                ? 'Signing…'
                                : null
                        }
                      >
                        {approvalRole === undefined ? 'Sign' : `Sign as ${approvalRole}`}
                      </PermissionButton>
                      <PermissionButton
                        variant="destructive"
                        onClick={() => reject.mutate(waiver.id)}
                        disabledReason={
                          !waiver.canSign
                            ? (waiver.cannotSignReason ?? 'You cannot decide on this waiver.')
                            : reject.isPending
                              ? 'Rejecting…'
                              : null
                        }
                      >
                        Reject
                      </PermissionButton>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}

        {(sign.isError || reject.isError) && (
          <p
            style={{
              margin: 0,
              padding: '9px 14px',
              fontSize: 11.5,
              color: 'var(--ba)',
              lineHeight: 1.5,
            }}
          >
            {(sign.error ?? reject.error) instanceof ApiClientError
              ? ((sign.error ?? reject.error) as ApiClientError).message
              : 'The change was not saved.'}
          </p>
        )}

        <p
          style={{
            margin: 0,
            padding: '9px 14px',
            fontSize: 11,
            color: 'var(--mu)',
            lineHeight: 1.5,
          }}
        >
          Two signatures, from two different people in two different roles — the database enforces
          both. Until the second arrives the waiver covers nothing.
        </p>
      </Card>

      {/*
        Adjacent on the screen, different in kind. A waiver is "we accept not meeting
        this"; not-applicable is "this was never in scope". Conflating them is how a
        report ends up claiming credit it has not earned.
      */}
      <Card
        title="Not applicable"
        action={<span style={CHIP}>{data.notApplicable.length} items</span>}
      >
        {data.notApplicable.length === 0 ? (
          <p style={{ margin: 0, padding: 14, fontSize: 12.5, color: 'var(--mu)' }}>
            Nothing is out of scope on this project.
          </p>
        ) : (
          data.notApplicable.map((entry) => (
            <div
              key={entry.itemRef}
              style={{
                display: 'flex',
                gap: 12,
                padding: '8px 14px',
                borderBottom: '1px solid var(--bd)',
                alignItems: 'flex-start',
              }}
            >
              <span
                className="mono"
                style={{
                  width: 44,
                  flex: '0 0 44px',
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: 'var(--mu)',
                }}
              >
                {entry.itemRef}
              </span>
              <div
                style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <span style={{ fontSize: 12.5 }}>{entry.statement}</span>
                <span style={{ fontSize: 11.5, color: 'var(--mu)', lineHeight: 1.5 }}>
                  {entry.justification}
                </span>
              </div>
              <span
                style={{
                  ...CHIP,
                  height: 18,
                  fontSize: 10,
                  ...(entry.fromContext
                    ? { background: 'var(--acs)', color: 'var(--ac)', borderColor: '#B9C4F5' }
                    : {}),
                }}
              >
                {entry.fromContext ? 'from context' : 'by hand'}
              </span>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
